import {
  DOCUMENT, Directive, ElementRef, ViewContainerRef, afterEveryRender, inject, inputBinding, outputBinding, signal,
} from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SbModalService } from '@app/_components/unified/sb-modal/sb-modal.service';
import { FontEntry, fontStack, loadFontCatalogue, loadFontFace } from './font-catalogue';
import { FontPickerClearComponent } from './font-picker-clear.component';
import { FontPickerDialogComponent } from './font-picker-dialog.component';

/**
 * Makes a text input a font field: a click (or Enter, Space or Down) opens the font dialog, and a clear button
 * follows it. A pick or a clear writes the bare family name, or '', to the input and fires a `change` event, as the
 * jQuery plugin did, so the field's `(change)` binding stores what it stored before. The field shows its font.
 */
@Directive({
  selector: '[appFontPicker]',
  host: {
    readonly: '',
    class: 'font-picker-field',
    'aria-haspopup': 'dialog',
    '(click)': 'open()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class FontPickerDirective {
  private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private readonly modal = inject(SbModalService);
  private readonly document = inject(DOCUMENT);

  private dialog: NgbModalRef | null = null;
  private shownFamily: string | null = null;
  /** The field's label and whether it is empty, for its clear button. */
  private readonly label = signal('');
  private readonly empty = signal(false);

  constructor() {
    inject(ViewContainerRef).createComponent(FontPickerClearComponent, {
      bindings: [
        inputBinding('field', this.label),
        inputBinding('empty', this.empty),
        outputBinding('cleared', () => this.clear()),
      ],
    });
    // The value comes from the template's binding, which sets the property without an event.
    afterEveryRender(() => this.showFont());
  }

  open(): void {
    if (this.dialog) {
      return;
    }
    this.dialog = this.modal.openComponent(FontPickerDialogComponent, {
      ariaLabelledBy: 'font-picker-title',
      windowClass: 'above-editor-modal font-picker-modal',
      backdropClass: 'above-editor-modal-backdrop',
    }, {value: this.el.nativeElement.value});
    this.dialog.result
      .then((family: string) => this.write(family), () => undefined)
      .finally(() => this.dialog = null);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.open();
    }
  }

  /** The clear button goes with the value, so focus stays on the field. */
  private clear(): void {
    this.write('');
    this.el.nativeElement.focus();
  }

  /** As the plugin's `onSelect` did: a non-bubbling `change` the field's own listener receives. */
  private write(family: string): void {
    this.el.nativeElement.value = family;
    this.el.nativeElement.dispatchEvent(new Event('change', {cancelable: true}));
    this.showFont();
  }

  private showFont(): void {
    const input = this.el.nativeElement;
    const family = input.value;
    this.label.set(input.labels?.[0]?.textContent.trim() ?? '');
    this.empty.set(!family);
    if (family === this.shownFamily) {
      return;
    }
    this.shownFamily = family;
    if (!family) {
      input.style.fontFamily = '';
      return;
    }
    // Without the font list the field still names its family, which shows an installed font.
    loadFontCatalogue().catch((): FontEntry[] => []).then((fonts) => {
      const font = fonts.find((entry) => entry.family === family);
      if (input.value !== family) {
        return;
      }
      if (font) {
        loadFontFace(font, this.document);
      }
      input.style.fontFamily = fontStack(family, font?.category);
    });
  }
}
